'use client'

import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Box, Button, CircularProgress, keyframes } from '@mui/material'
import { alpha } from '@mui/material/styles'
import { Refresh, VolumeOff, VolumeUp } from '@mui/icons-material'
import Image from 'next/image'
import type { MediaPlayerProps } from '../types'
import {
    MEDIA_FLOATER_FALLBACK_IMAGE,
    MEDIA_FLOATER_MAX_RETRIES
} from '../constants'

/** Fade-in then fade-out for the center mute indicator */
const muteIndicatorAnim = keyframes`
    0%   { opacity: 0; transform: translate(-50%, -50%) scale(0.5); }
    15%  { opacity: 1; transform: translate(-50%, -50%) scale(1);   }
    70%  { opacity: 1; transform: translate(-50%, -50%) scale(1);   }
    100% { opacity: 0; transform: translate(-50%, -50%) scale(0.8); }
`

/**
 * MediaPlayer — playback, load/retry and mute state for a single media item.
 *
 * When `tapToMute` is true (used in ReelViewer), clicking anywhere on the video
 * toggles mute and flashes a centered volume icon — no persistent button shown.
 */
const MediaPlayer: React.FC<MediaPlayerProps> = ({
    mediaItem,
    onNext,
    autoPlay = true,
    isInModal = false,
    priority = false,
    onDurationChange,
    muteButtonPosition = 'bottom-left',
    tapToMute = false
}) => {
    const [isLoading, setIsLoading] = useState(true)
    const [hasError, setHasError] = useState(false)
    const [retryCount, setRetryCount] = useState(0)
    const [imageSrc, setImageSrc] = useState(mediaItem.src)
    const [isMuted, setIsMuted] = useState(true)
    /** Counter that increments on each tap; drives the flash animation key */
    const [muteFlashKey, setMuteFlashKey] = useState(0)
    const [showMuteFlash, setShowMuteFlash] = useState(false)
    const videoRef = useRef<HTMLVideoElement>(null)
    const flashTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

    useEffect(() => {
        setIsLoading(true)
        setHasError(false)
        setRetryCount(0)
        setImageSrc(mediaItem.src)
        setIsMuted(true)
        setShowMuteFlash(false)
    }, [mediaItem])

    useEffect(() => {
        if (videoRef.current) {
            videoRef.current.muted = isMuted
        }
    }, [isMuted, mediaItem])

    useEffect(() => {
        if (autoPlay && mediaItem.type === 'image' && mediaItem.duration) {
            const timer = setTimeout(onNext, mediaItem.duration)
            return () => clearTimeout(timer)
        }
    }, [mediaItem, onNext, autoPlay])

    // Programmatically play video — MUI Box with component="video" doesn't forward
    // the autoPlay attribute reliably to the DOM element.
    useEffect(() => {
        if (autoPlay && mediaItem.type === 'video' && isInModal && videoRef.current) {
            videoRef.current.play().catch(() => {})
        }
    }, [autoPlay, mediaItem, isInModal])

    const handleVideoError = useCallback(() => {
        if (retryCount < MEDIA_FLOATER_MAX_RETRIES && videoRef.current) {
            setRetryCount((prev) => prev + 1)
            setIsLoading(true)
            setHasError(false)
            videoRef.current.load()
        } else {
            setIsLoading(false)
            setHasError(true)
        }
    }, [retryCount])

    const handleVideoLoadedMetadata = useCallback(
        (e: React.SyntheticEvent<HTMLVideoElement>) => {
            const video = e.currentTarget
            if (video.duration && onDurationChange) {
                onDurationChange(video.duration * 1000)
            }
        },
        [onDurationChange]
    )

    /** Toggle mute via the dedicated IconButton (non-tapToMute mode) */
    const handleToggleMute = useCallback((e: React.MouseEvent) => {
        e.stopPropagation()
        setIsMuted((prev) => !prev)
    }, [])

    /** Toggle mute by tapping the video area — shows a flash indicator */
    const handleTapToMute = useCallback(() => {
        setIsMuted((prev) => !prev)
        setShowMuteFlash(true)
        setMuteFlashKey((k) => k + 1)
        if (flashTimerRef.current) clearTimeout(flashTimerRef.current)
        flashTimerRef.current = setTimeout(() => setShowMuteFlash(false), 800)
    }, [])

    // Clean up timer on unmount
    useEffect(() => {
        return () => {
            if (flashTimerRef.current) clearTimeout(flashTimerRef.current)
        }
    }, [])

    /** Centered flash indicator for tap-to-mute */
    const muteFlashIndicator = showMuteFlash && (
        <Box
            key={muteFlashKey}
            sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                zIndex: 8,
                width: 72,
                height: 72,
                borderRadius: '50%',
                bgcolor: (theme) => alpha(theme.palette.common.black, 0.55),
                backdropFilter: 'blur(6px)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                pointerEvents: 'none',
                animation: `${muteIndicatorAnim} 0.8s ease-out forwards`
            }}
        >
            {isMuted ? (
                <VolumeOff sx={{ color: 'common.white', fontSize: 32 }} />
            ) : (
                <VolumeUp sx={{ color: 'common.white', fontSize: 32 }} />
            )}
        </Box>
    )

    if (mediaItem.type === 'image') {
        return (
            <Box
                sx={{
                    width: '100%',
                    height: '100%',
                    position: 'relative',
                    bgcolor: 'common.black'
                }}
            >
                <Image
                    src={imageSrc}
                    alt={mediaItem.alt || 'Media content'}
                    fill
                    sizes="(max-width: 768px) 250px, 300px"
                    style={{ objectFit: 'cover' }}
                    loading={priority ? 'eager' : 'lazy'}
                    priority={priority}
                    onLoad={() => setIsLoading(false)}
                    onError={() => {
                        if (retryCount < MEDIA_FLOATER_MAX_RETRIES) {
                            setRetryCount((prev) => prev + 1)
                            setIsLoading(true)
                            setHasError(false)
                            setImageSrc(`${mediaItem.src}?retry=${retryCount + 1}&t=${Date.now()}`)
                        } else {
                            setIsLoading(false)
                            setHasError(true)
                            setImageSrc(MEDIA_FLOATER_FALLBACK_IMAGE)
                        }
                    }}
                />
                {isLoading && !hasError && (
                    <Box
                        sx={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            zIndex: 1
                        }}
                    >
                        <CircularProgress size={24} sx={{ color: 'common.white' }} />
                    </Box>
                )}
                {hasError && imageSrc !== MEDIA_FLOATER_FALLBACK_IMAGE && (
                    <Box
                        sx={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            color: 'common.white',
                            textAlign: 'center',
                            p: 2,
                            zIndex: 2
                        }}
                    >
                        <Box sx={{ mb: 2 }}>Failed to load image</Box>
                        <Button
                            variant="contained"
                            size="small"
                            startIcon={<Refresh />}
                            onClick={() => {
                                setRetryCount(0)
                                setIsLoading(true)
                                setHasError(false)
                                setImageSrc(mediaItem.src)
                            }}
                            sx={{ bgcolor: 'primary.main', '&:hover': { bgcolor: 'primary.dark' } }}
                        >
                            Retry
                        </Button>
                    </Box>
                )}
            </Box>
        )
    }

    // ── Video (not in modal — floater preview) ──
    if (!isInModal) {
        return (
            <Box
                sx={{
                    width: '100%',
                    height: '100%',
                    position: 'relative',
                    bgcolor: 'common.black'
                }}
            >
                <Box
                    ref={videoRef}
                    component="video"
                    src={mediaItem.src}
                    autoPlay
                    muted={isMuted}
                    playsInline
                    onEnded={onNext}
                    onLoadedMetadata={handleVideoLoadedMetadata}
                    sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
            </Box>
        )
    }

    // ── Video (in modal / reel viewer) ──
    return (
        <Box
            onClick={tapToMute ? handleTapToMute : undefined}
            sx={{
                width: '100%',
                height: '100%',
                position: 'relative',
                bgcolor: 'common.black',
                cursor: tapToMute ? 'pointer' : 'default'
            }}
        >
            <Box
                ref={videoRef}
                component="video"
                src={mediaItem.src}
                autoPlay={autoPlay}
                muted={isMuted}
                playsInline
                onEnded={onNext}
                onError={handleVideoError}
                onCanPlay={() => {
                    setIsLoading(false)
                    setHasError(false)
                }}
                onPlaying={() => {
                    setIsLoading(false)
                    setHasError(false)
                }}
                onLoadedMetadata={handleVideoLoadedMetadata}
                sx={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    opacity: hasError ? 0 : 1
                }}
            />
            {isLoading && !hasError && (
                <Box
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        zIndex: 1
                    }}
                >
                    <CircularProgress size={24} sx={{ color: 'common.white' }} />
                </Box>
            )}
            {hasError && (
                <Box
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        color: 'common.white',
                        textAlign: 'center',
                        p: 2,
                        zIndex: 2
                    }}
                >
                    <Box sx={{ mb: 2 }}>Failed to load video</Box>
                    <Button
                        variant="contained"
                        size="small"
                        startIcon={<Refresh />}
                        onClick={() => {
                            if (videoRef.current) {
                                setRetryCount(0)
                                setIsLoading(true)
                                setHasError(false)
                                videoRef.current.load()
                            }
                        }}
                        sx={{ bgcolor: 'primary.main', '&:hover': { bgcolor: 'primary.dark' } }}
                    >
                        Retry
                    </Button>
                </Box>
            )}
            {tapToMute && muteFlashIndicator}
        </Box>
    )
}

export { MediaPlayer }
export type { MediaPlayerProps } from '../types'
